<script setup lang="ts">
import { userRolesList } from "@/APIClient";
import { getErrorMessage } from "@/utils/misc.utils";
import { Logger } from "@/logger";
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import DRBtn from "../UI/DRBtn.vue";
import UserRoleBadge from "./UserRoleBadge.vue";
import { useValidation } from "@/composable/useValidation";
import { useAddMember } from "@/stores/queries";
import OrganizationPageLayout from "@/components/organization/OrganizationPageLayout.vue";

const { t } = useTranslation();

const { mutateAsync: addMember, isPending: isLoading } = useAddMember();

const form = reactive({
  name: "",
  email: "",
  role: "",
  password: "",
  confirmation: "",
});

const { check, getMessage } = useValidation(
  form,
  ({ required, email, password, custom }) => ({
    name: { required },
    email: {
      required,
      email,
    },
    role: { required },
    password: {
      required,
      password,
    },
    confirmation: {
      required,
      sameAs: custom(
        "Le mot de passe et la confirmation ne correspondent pas",
        (value: string) => value === form.password
      ),
    },
  })
);

const showError = ref("");

const router = useRouter();

async function submit() {
  const isValid = check();
  if (isValid) {
    showError.value = "";
  } else {
    return;
  }

  addMember(
    {
      ...form,
    },
    {
      onSuccess: () => {
        router.back();
      },
      onError: (error) => {
        Logger.error(
          `Register.vue : Couldn't sign up: ${JSON.stringify(error.message)}`
        );
        showError.value = getErrorMessage(error, {
          auth: "Un compte existe déjà avec cette adresse.",
        });
      },
    }
  );
}

const unauthorizedRoles = ["SUPERADMIN", "ADMIN"];

const roleOptions = userRolesList.filter(
  (role) => !unauthorizedRoles.includes(role)
);
</script>

<template>
  <OrganizationPageLayout
    :title="t('dashboard:navigation.new-member')"
    :description="t('dashboard:navigation.new-member-description')"
  >
    <div class="d-flex justify-content-center">
      <el-form
        label-width="auto"
        :model="form"
        label-position="left"
        style="max-width: 600px; width: 100%"
        id="signup-form"
        @submit.prevent="submit"
      >
        <el-form-item
          required
          label="Nom et prénom"
          :error="getMessage('name')"
        >
          <el-input
            v-model="form.name"
            type="text"
            id="name"
            size="large"
            placeholder="Nom et prénom"
          />
        </el-form-item>

        <el-form-item
          required
          label="Adresse email"
          :error="getMessage('email')"
        >
          <el-input
            v-model="form.email"
            type="email"
            id="email"
            placeholder="exemple@gmail.com"
            size="large"
          />
        </el-form-item>

        <el-form-item required label="Rôle" :error="getMessage('role')">
          <el-select
            v-model="form.role"
            placeholder="Sélectionner le rôle"
            size="large"
          >
            <el-option
              v-for="role in roleOptions"
              :key="role"
              :label="t('dashboard.userRole.' + role)"
              :value="role"
            >
              <UserRoleBadge :role="role" />
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          required
          :label="t('password')"
          :error="getMessage('password')"
        >
          <el-input
            v-model="form.password"
            type="password"
            id="password"
            placeholder="Saisir le mot de passe"
            size="large"
          />
        </el-form-item>

        <el-form-item
          required
          label="Confirmer le mot de passe"
          :error="getMessage('confirmation')"
        >
          <el-input
            v-model="form.confirmation"
            type="password"
            id="confirmation"
            title="Les mots de passe ne correspondent pas."
            size="large"
            placeholder="Confirmer le mot de passe"
          />
        </el-form-item>

        <div
          v-if="showError !== ''"
          class="alert alert-danger mt-3"
          role="alert"
        >
          {{ showError }}
        </div>

        <div
          class="d-flex justify-content-between align-items-center gap-2 mt-5"
        >
          <DRBtn
            variant="outline"
            icon="arrow-left"
            :label="t('back')"
            @click="$router.back()"
          />
          <DRBtn
            :loading="isLoading"
            variant="primary"
            submit
            icon="plus"
            label="Créer un compte"
          />
        </div>
      </el-form>
    </div>
  </OrganizationPageLayout>
</template>
